// Make changes › "What people enter" — the kind of a field in plain words,
// mapped to and from the model by DATA (./fieldKinds.json). Pure functions.

import data from './fieldKinds.json'
import type { EditMaskValue } from '../../txn/types'

export interface FieldKind {
  id: string
  label: string
  line: string
  col_type: string
  db_size: string
  mask: string
  lookup?: boolean
  values?: EditMaskValue[]
  decimals?: number
  display?: Record<string, unknown>
  match: { lookup?: boolean; mask?: string[]; col_type?: string[]; db_size?: string[]; display?: string[] }
}

export const FIELD_KINDS: FieldKind[] = (data as { kinds: FieldKind[] }).kinds
export const DECIMAL_CHOICES: number[] = (data as { decimal_choices: number[] }).decimal_choices
export const WIDTH_CHOICES: { id: string; label: string; width: number }[] =
  (data as { width_choices: { id: string; label: string; width: number }[] }).width_choices
export interface StartsWith { id: string; label: string; value?: string; datetime_value?: string; kinds?: string[] }
export const STARTS_WITH: StartsWith[] = (data as { starts_with: StartsWith[] }).starts_with
export const NEXT_NUMBER = (data as {
  next_number: { digits: number; year_choices: { id: '' | 'YYYY' | 'YY'; label: string }[] }
}).next_number

/** A column as these helpers read it. */
export interface KindColumn {
  col_type?: string
  db_size?: string
  edit_mask?: { mask?: string; values?: EditMaskValue[] } | null
  lookup?: string
  lookup_sql?: string
  display?: { type?: string } | null
}

const up = (v: unknown) => String(v ?? '').trim().toUpperCase()

export function kindById(id: string): FieldKind | undefined {
  return FIELD_KINDS.find((k) => k.id === id)
}

/** The kind a field is, recognised from its model (first match wins). */
export function kindOf(col: KindColumn): FieldKind {
  const mask = String(col.edit_mask?.mask ?? '').trim()
  const hasLookup = !!String(col.lookup ?? '').trim() || !!String(col.lookup_sql ?? '').trim()
  const display = String(col.display?.type ?? '').trim()
  const size = String(col.db_size ?? '').replace(/\s+/g, '')
  for (const k of FIELD_KINDS) {
    const m = k.match
    if (m.lookup !== undefined && m.lookup !== hasLookup) continue
    if (m.mask && !m.mask.includes(mask)) continue
    if (m.col_type && !m.col_type.includes(up(col.col_type))) continue
    if (m.db_size && !m.db_size.includes(size)) continue
    if (m.display && !m.display.includes(display)) continue
    // A plain-text kind must not swallow a choice / lookup field.
    if (!m.lookup && hasLookup) continue
    if (!m.mask && ['ddlb', 'radio', 'bool_check', 'checkbox', 'textarea', 'image', 'document'].includes(mask)) continue
    return k
  }
  return FIELD_KINDS[FIELD_KINDS.length - 1]
}

/** Decimal places a number field has (its db_size "p,s"). */
export function decimalsOf(col: KindColumn): number {
  const m = String(col.db_size ?? '').match(/^\s*\d+\s*,\s*(\d+)\s*$/)
  return m ? Number(m[1]) : 0
}

/** db_size for a number with `decimals` places. */
export function numberSize(decimals: number): string {
  return decimals > 0 ? `14,${decimals}` : '12'
}

export interface LookupSpec { table: string; display: string; data: string }

export function parseLookup(spec: string | undefined | null): LookupSpec | null {
  const m = String(spec ?? '').trim().match(
    /^([A-Za-z_][A-Za-z0-9_]*)\s*\(\s*([A-Za-z_][A-Za-z0-9_]*)\s*,\s*([A-Za-z_][A-Za-z0-9_]*)\s*\)\s*$/,
  )
  return m ? { table: m[1], display: m[2], data: m[3] } : null
}

export function lookupSpec(l: LookupSpec): string {
  return `${l.table}(${l.display}, ${l.data})`
}

/** The column leaves a kind writes (what changes when "What people enter"
 *  is picked). `values` keeps a choice list's choices; `lookup` a picked-from
 *  screen. Leaves the kind does not use are cleared so nothing stale stays. */
export function kindLeaves(kindId: string, opts: {
  values?: EditMaskValue[]
  lookup?: LookupSpec | null
  decimals?: number
} = {}): Record<string, unknown> {
  const k = kindById(kindId) ?? FIELD_KINDS[FIELD_KINDS.length - 1]
  const decimals = opts.decimals ?? k.decimals
  const size = k.id === 'number' && decimals !== undefined ? numberSize(decimals)
    : k.id === 'money' && decimals !== undefined ? `14,${decimals}` : k.db_size
  const values = k.values ?? (k.mask === 'ddlb' ? (opts.values ?? []) : [])
  const leaves: Record<string, unknown> = {
    col_type: k.col_type,
    db_size: size,
    edit_mask: { mask: k.mask, values: k.mask === 'ddlb' ? (opts.values ?? values) : values },
    lookup: k.lookup && opts.lookup ? lookupSpec(opts.lookup) : '',
    display: k.display ? { ...k.display, ...(k.id === 'money' && decimals !== undefined ? { decimals } : {}) } : null,
  }
  return leaves
}

/** A stored name from what people call a field: lower case, words joined
 *  by _, unique against `taken` (lower-cased). */
export function storedName(label: string, taken: Iterable<string>): string {
  const used = new Set(Array.from(taken, (t) => String(t).toLowerCase()))
  let base = String(label || '').trim().toLowerCase()
    .normalize('NFKD').replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 40)
  if (!base) base = 'field'
  if (/^[0-9]/.test(base)) base = `f_${base}`
  let name = base
  for (let n = 2; used.has(name); n++) name = `${base}_${n}`
  return name
}

/** A new column for a form, ready to insert. */
export function newColumn(opts: {
  label: string
  kindId: string
  table: string
  group: string
  taken: Iterable<string>
  mandatory?: boolean
  values?: EditMaskValue[]
  lookup?: LookupSpec | null
  y?: number
  x?: number
}): Record<string, unknown> {
  const db = storedName(opts.label, opts.taken)
  const leaves = kindLeaves(opts.kindId, { values: opts.values, lookup: opts.lookup })
  const col: Record<string, unknown> = {
    name: db,
    col_id: db,
    db_name: db,
    table_name: opts.table,
    heading: opts.label.trim(),
    descr: '',
    group_name: opts.group === 'Others' ? '' : opts.group,
    key: false,
    mandatory: !!opts.mandatory,
    protect: false,
    hidden: false,
    default_value: '',
    width: 200,
    x: String(opts.x ?? 10),
    y: String(opts.y ?? 0),
    format: '',
    item_change: null,
    validations: [],
    ...leaves,
  }
  if (!col.display) delete col.display
  return col
}

export const FIELD_SUGGESTIONS: { label: string; kind: string }[] =
  (data as { suggestions?: { label: string; kind: string }[] }).suggestions || []
